import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFindMany, useAction, useSession } from '@gadgetinc/react';
import { api } from '../api';

const HITLQueue = () => {
  const navigate = useNavigate();
  const session = useSession();
  const [sortBy, setSortBy] = useState({ field: 'priority', direction: 'desc' });
  const [filters, setFilters] = useState({
    priority: '',
    dateRange: '',
    reviewer: '',
    showFilters: false
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [reviewModal, setReviewModal] = useState({ isOpen: false, item: null });

  // Fetch HITL reviews
  const [{ data: hitlReviews, fetching, error }, refetch] = useFindMany(api.hitlReview, {
    filter: { 
      resolved: { equals: false }
    },
    select: {
      id: true,
      createdAt: true,
      updatedAt: true,
      patientId: true,
      priority: true,
      reason: true,
      resolved: true,
      resolvedAt: true,
      resolvedBy: {
        id: true,
        firstName: true,
        lastName: true,
        email: true
      },
      shiftId: true,
      documentProcessing: {
        id: true,
        originalFilename: true,
        submissionEmail: true,
        processingStatus: true,
        structuredData: true
      },
      clinicalEvaluation: {
        id: true,
        modelOutput: true,
        passStatus: true,
        needsHitl: true
      },
      reviewerNotes: {
        markdown: true
      }
    },
    sort: [
      { [sortBy.field]: sortBy.direction === 'desc' ? 'Descending' : 'Ascending' },
      { createdAt: 'Descending' }
    ],
    live: true
  });

  // Actions
  const [{ data: updateData, fetching: updating, error: updateError }, updateReview] = useAction(api.hitlReview.update);

  // Filter and sort data
  const filteredReviews = useMemo(() => {
    if (!hitlReviews) return [];
    
    return hitlReviews.filter(review => {
      if (filters.priority && review.priority !== filters.priority) return false;
      if (filters.reviewer && (!review.resolvedBy || !review.resolvedBy.email.includes(filters.reviewer))) return false;
      if (filters.dateRange) {
        const reviewDate = new Date(review.createdAt);
        const now = new Date();
        const daysAgo = parseInt(filters.dateRange);
        const cutoffDate = new Date(now.setDate(now.getDate() - daysAgo));
        if (reviewDate < cutoffDate) return false;
      }
      return true;
    });
  }, [hitlReviews, filters]);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-500';
      case 'urgent': return 'text-red-600 font-bold';
      case 'medium': return 'text-yellow-500';
      case 'low': return 'text-green-500';
      default: return 'text-gray-500';
    }
  };

  const getPriorityBadgeColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'urgent': return 'bg-red-200 text-red-900';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleSort = (field) => {
    setSortBy(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(filteredReviews.map(review => review.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const handleResolveReview = async (reviewId, notes = '') => {
    try {
      await updateReview({
        id: reviewId,
        resolved: true,
        resolvedAt: new Date(),
        resolvedBy: { _link: session.user?.id },
        reviewerNotes: notes ? { markdown: notes } : undefined
      });
      await refetch();
      setReviewModal({ isOpen: false, item: null });
    } catch (error) {
      console.error('Failed to resolve review:', error);
    }
  };

  const handleBulkResolve = async () => {
    try {
      await Promise.all(selectedItems.map(id => 
        updateReview({
          id,
          resolved: true,
          resolvedAt: new Date(),
          resolvedBy: { _link: session.user?.id }
        })
      ));
      await refetch();
      setSelectedItems([]);
    } catch (error) {
      console.error('Failed to bulk resolve:', error);
    }
  };

  const openReviewModal = (item) => {
    setReviewModal({ isOpen: true, item });
  };

  const closeReviewModal = () => {
    setReviewModal({ isOpen: false, item: null });
  };

  if (fetching && !hitlReviews) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-lg">Loading HITL queue...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-red-400 text-lg">Error loading queue: {error.message}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 p-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => navigate('/admin')}
              className="text-gray-400 hover:text-white transition-colors"
            >
              ← Back to Admin
            </button>
            <h1 className="text-2xl font-bold">HITL Review Queue</h1>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-gray-400">
                {filteredReviews?.length || 0} pending reviews
              </span>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex items-center space-x-3">
            {selectedItems.length > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-400">
                  {selectedItems.length} selected
                </span>
                <button
                  onClick={handleBulkResolve}
                  disabled={updating}
                  className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 px-3 py-1 rounded text-sm transition-colors"
                >
                  Resolve Selected
                </button>
              </div>
            )}
            <button
              onClick={() => setFilters(prev => ({ ...prev, showFilters: !prev.showFilters }))}
              className="bg-gray-700 hover:bg-gray-600 px-3 py-2 rounded transition-colors"
            >
              Filters
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      {filters.showFilters && (
        <div className="bg-gray-800 border-b border-gray-700 p-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Priority</label>
                <select
                  value={filters.priority}
                  onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                >
                  <option value="">All Priorities</option>
                  <option value="urgent">Urgent</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Date Range</label>
                <select
                  value={filters.dateRange}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                >
                  <option value="">All Dates</option>
                  <option value="1">Last 24 hours</option>
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Reviewer</label>
                <input
                  type="text"
                  placeholder="Search by reviewer email..."
                  value={filters.reviewer}
                  onChange={(e) => setFilters(prev => ({ ...prev, reviewer: e.target.value }))}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white placeholder-gray-500"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-4">
        {filteredReviews && filteredReviews.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">✅</div>
            <h2 className="text-xl font-semibold mb-2">No pending reviews!</h2>
            <p className="text-gray-400">All HITL reviews have been resolved.</p>
          </div>
        ) : (
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="p-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedItems.length === filteredReviews?.length && filteredReviews?.length > 0}
                        onChange={handleSelectAll}
                        className="rounded border-gray-600 bg-gray-700 text-blue-600"
                      />
                    </th>
                    <th 
                      className="p-3 text-left cursor-pointer hover:bg-gray-600 transition-colors"
                      onClick={() => handleSort('patientId')}
                    >
                      Patient ID {sortBy.field === 'patientId' && (sortBy.direction === 'desc' ? '↓' : '↑')}
                    </th>
                    <th 
                      className="p-3 text-left cursor-pointer hover:bg-gray-600 transition-colors"
                      onClick={() => handleSort('shiftId')}
                    >
                      Shift ID {sortBy.field === 'shiftId' && (sortBy.direction === 'desc' ? '↓' : '↑')}
                    </th>
                    <th className="p-3 text-left">Document Type</th>
                    <th 
                      className="p-3 text-left cursor-pointer hover:bg-gray-600 transition-colors"
                      onClick={() => handleSort('priority')}
                    >
                      Priority {sortBy.field === 'priority' && (sortBy.direction === 'desc' ? '↓' : '↑')}
                    </th>
                    <th 
                      className="p-3 text-left cursor-pointer hover:bg-gray-600 transition-colors"
                      onClick={() => handleSort('createdAt')}
                    >
                      Created {sortBy.field === 'createdAt' && (sortBy.direction === 'desc' ? '↓' : '↑')}
                    </th>
                    <th className="p-3 text-left">Assigned Reviewer</th>
                    <th className="p-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {filteredReviews?.map((review) => (
                    <tr key={review.id} className="hover:bg-gray-750 transition-colors">
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(review.id)}
                          onChange={() => handleSelectItem(review.id)}
                          className="rounded border-gray-600 bg-gray-700 text-blue-600"
                        />
                      </td>
                      <td className="p-3 font-mono text-sm">{review.patientId}</td>
                      <td className="p-3 font-mono text-sm">{review.shiftId || 'N/A'}</td>
                      <td className="p-3">
                        {review.documentProcessing?.originalFilename || 'Unknown'}
                      </td>
                      <td className="p-3">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getPriorityBadgeColor(review.priority)}`}>
                          {review.priority}
                        </span>
                      </td>
                      <td className="p-3 text-sm text-gray-400">
                        {new Date(review.createdAt).toLocaleString()}
                      </td>
                      <td className="p-3 text-sm">
                        {review.resolvedBy ? 
                          `${review.resolvedBy.firstName} ${review.resolvedBy.lastName}` : 
                          'Unassigned'
                        }
                      </td>
                      <td className="p-3">
                        <button
                          onClick={() => openReviewModal(review)}
                          className="bg-blue-600 hover:bg-blue-700 px-3 py-1 rounded text-sm transition-colors"
                        >
                          Review
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden space-y-4 p-4">
              {filteredReviews?.map((review) => (
                <div key={review.id} className="bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(review.id)}
                        onChange={() => handleSelectItem(review.id)}
                        className="rounded border-gray-600 bg-gray-700 text-blue-600"
                      />
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getPriorityBadgeColor(review.priority)}`}>
                        {review.priority}
                      </span>
                    </div>
                    <button
                      onClick={() => openReviewModal(review)}
                      className="bg-blue-600 hover:bg-blue-700 px-3 py-1 rounded text-sm transition-colors"
                    >
                      Review
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-400">Patient:</span>
                      <span className="ml-1 font-mono">{review.patientId}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Shift:</span>
                      <span className="ml-1 font-mono">{review.shiftId || 'N/A'}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-400">Document:</span>
                      <span className="ml-1">{review.documentProcessing?.originalFilename || 'Unknown'}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-400">Created:</span>
                      <span className="ml-1">{new Date(review.createdAt).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Review Modal */}
      {reviewModal.isOpen && reviewModal.item && (
        <ReviewModal
          item={reviewModal.item}
          onClose={closeReviewModal}
          onResolve={handleResolveReview}
          updating={updating}
        />
      )}
    </div>
  );
};

// Review Modal Component
const ReviewModal = ({ item, onClose, onResolve, updating }) => {
  const [notes, setNotes] = useState(item.reviewerNotes?.markdown || '');
  const [decision, setDecision] = useState('');

  const handleSubmit = async () => {
    if (!decision) return;
    
    if (decision === 'resolve') {
      await onResolve(item.id, notes);
    }
    // Add escalate logic here if needed
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-500';
      case 'urgent': return 'text-red-600 font-bold';
      case 'medium': return 'text-yellow-500';
      case 'low': return 'text-green-500';
      default: return 'text-gray-500';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold">Review Clinical Document</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Document Info */}
            <div>
              <h3 className="text-lg font-medium mb-4">Document Details</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-gray-400">Patient ID:</span>
                  <span className="ml-2 font-mono">{item.patientId}</span>
                </div>
                <div>
                  <span className="text-gray-400">Shift ID:</span>
                  <span className="ml-2 font-mono">{item.shiftId || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-gray-400">Document:</span>
                  <span className="ml-2">{item.documentProcessing?.originalFilename}</span>
                </div>
                <div>
                  <span className="text-gray-400">Priority:</span>
                  <span className={`ml-2 font-medium ${getPriorityColor(item.priority)}`}>
                    {item.priority}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">Reason for Review:</span>
                  <span className="ml-2">{item.reason}</span>
                </div>
              </div>

              {/* AI Processing Results */}
              {item.clinicalEvaluation?.modelOutput && (
                <div className="mt-6">
                  <h4 className="text-md font-medium mb-2">AI Processing Results</h4>
                  <div className="bg-gray-700 rounded p-3 text-sm">
                    <div dangerouslySetInnerHTML={{ 
                      __html: item.clinicalEvaluation.modelOutput.truncatedHTML || 
                             item.clinicalEvaluation.modelOutput.markdown 
                    }} />
                  </div>
                </div>
              )}
            </div>

            {/* Review Form */}
            <div>
              <h3 className="text-lg font-medium mb-4">Review & Resolution</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Decision
                  </label>
                  <select
                    value={decision}
                    onChange={(e) => setDecision(e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                  >
                    <option value="">Select decision...</option>
                    <option value="resolve">Resolve - Accept AI Results</option>
                    <option value="resolve-with-changes">Resolve - With Manual Changes</option>
                    <option value="escalate">Escalate to Senior Reviewer</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Reviewer Notes
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add your review notes here..."
                    rows={6}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white placeholder-gray-500 resize-none"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            disabled={updating}
            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!decision || updating}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 px-4 py-2 rounded transition-colors"
          >
            {updating ? 'Processing...' : 'Submit Review'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HITLQueue;